import { makeAutoObservable } from "mobx";

import keys from "../config/keys";
import { uuidv4 } from "../utils";

import type { Timestamp } from "../types";

type LogEventSeverity = "info" | "success" | "warning" | "error";

type LogEvent = {
  id: string;
  severity: LogEventSeverity;
  message: string;
  payload: any;
  createdAt: Timestamp;
};

export default class LogStore {
  isInitialized: boolean = false;
  logs: LogEvent[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  initialize = async () => {
    this.isInitialized = true;
  };

  destroy = async (): Promise<void> => {
    this.logs = [];
  };

  log = async (
    severity: LogEvent["severity"],
    message: LogEvent["message"],
    payload: LogEvent["payload"]
  ): Promise<void> => {
    if (keys.LOGS_RETENTION_MILLISECONDS <= 0) {
      return;
    }
    const createdLog = {
      id: uuidv4(),
      severity,
      message,
      payload,
      createdAt: Date.now(),
    };
    const nextLogs = this.logs
      .filter(
        (log) => Date.now() - log.createdAt < keys.LOGS_RETENTION_MILLISECONDS
      )
      .concat([createdLog]);
    this.logs = nextLogs;
  };

  info = async (
    message: LogEvent["message"],
    payload: LogEvent["payload"]
  ): Promise<void> => {
    await this.log("info", message, payload);
  };

  success = async (
    message: LogEvent["message"],
    payload: LogEvent["payload"]
  ): Promise<void> => {
    await this.log("success", message, payload);
  };

  warning = async (
    message: LogEvent["message"],
    payload: LogEvent["payload"]
  ): Promise<void> => {
    await this.log("warning", message, payload);
  };

  error = async (
    message: LogEvent["message"],
    payload: LogEvent["payload"]
  ): Promise<void> => {
    await this.log("error", message, payload);
  };
}
